// Zweck: Vaillant-Registrierung mit eindeutiger Konfliktbehandlung.
// Kontext: Ein bestätigter 409-Konflikt ist kein transienter Upload-Fehler.
// Stand: 2026-10-27
import one360 from './one360';
import type { RegisterVaillantSerialNumberResult } from '../../interfaces/vaillant';

/**
 * `idempotencyKey`: von der One360-API bereitgestellter Dedup-Mechanismus (#1202, Antwort auf
 * das Backend-Ticket zu #1199) — pro Registrierungsversuch einmal erzeugt und bei jedem Retry
 * (auch aus der Offline-Queue, siehe queueService.processVaillantSerialRegister) unverändert
 * mitgeschickt. Ein wiederholter Aufruf mit demselben Schlüssel für dieselbe Seriennummer
 * kontaktiert Vaillant NICHT erneut, sondern liefert das ursprüngliche Ergebnis zurück -
 * schließt die Restlücke, die die manuelle Bestätigung in PhaseScreen bisher nur clientseitig
 * absichern konnte. Optional gehalten: ohne Key bleibt der Aufruf unverändert (Rückwärts-
 * kompatibilität laut Backend-Contract).
 */
export async function registerVaillantSerialNumber(
  serialNumber: string,
  email?: string,
  signal?: AbortSignal,
  idempotencyKey?: string,
): Promise<RegisterVaillantSerialNumberResult> {
  return one360.post<RegisterVaillantSerialNumberResult>(
    '/api/v1/vaillant/register-serial-number',
    { serialNumber, email },
    {
      signal,
      headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : undefined,
    },
  ).catch((error: unknown) => {
    // Der aktuelle API-Vertrag verpackt die explizite Vaillant-Ablehnung in HTTP 500.
    // Übergangsvertrag zu One360 #1202 (CallVaillantRegisterApiCommand): erst bei
    // einem stabilen fachlichen Fehlercode ablösen, nicht allein bei echtem HTTP 409.
    // Absichtlich exakt: Präfixe/andere Meldungen könnten einen unklaren Ausgang
    // beschreiben und dürfen nicht zum permanenten Verwerfen eines Auftrags führen.
    const failure = error as { status?: unknown; message?: unknown } | null;
    if ((failure?.status === 409 || failure?.status === 500) &&
      failure.message === 'Vaillant serial number registration failed with status 409.') {
      throw Object.assign(new Error(
        'Vaillant meldet einen Registrierungskonflikt (409). Bitte den Registrierungsstatus der Anlage prüfen, bevor erneut registriert wird. Die Registrierung wurde nicht als erfolgreich bestätigt.',
      ), { status: 409, isVaillantRegistrationConflict: true });
    }
    throw error;
  });
}

export function isVaillantRegistrationConflict(error: unknown): error is Error {
  return error instanceof Error &&
    (error as { isVaillantRegistrationConflict?: unknown }).isVaillantRegistrationConflict === true;
}
